import type { PatternTool } from '@miyuki/domain';
import type { PaletteDto } from '@miyuki/types';
import { Button } from '@miyuki/ui';
import { MaximizeIcon, PlusIcon, Redo2Icon, Undo2Icon } from 'lucide-react';

import { PaletteStrip } from './palette-strip';

import { t } from '@/lib/i18n';

/**
 * Franja inferior: la paleta y las acciones que se usan dibujando.
 *
 * Está abajo porque es donde alcanza el pulgar, y no en un costado porque los
 * costados quedan libres para no tapar el diseño (RF-46).
 */
export interface BottomBarProps {
  readonly palette: PaletteDto;
  readonly tool: PatternTool;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly canAddRow: boolean;
  readonly onSelectTool: (tool: PatternTool) => void;
  readonly onEditColor: (index: number) => void;
  readonly onAddColor: () => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly onFitToScreen: () => void;
  readonly onAddRow: () => void;
}

export function BottomBar({
  palette,
  tool,
  canUndo,
  canRedo,
  canAddRow,
  onSelectTool,
  onEditColor,
  onAddColor,
  onUndo,
  onRedo,
  onFitToScreen,
  onAddRow,
}: BottomBarProps) {
  return (
    <footer className="bg-background/90 border-border shrink-0 border-t backdrop-blur">
      <PaletteStrip
        palette={palette}
        tool={tool}
        onSelect={onSelectTool}
        onEdit={onEditColor}
        onAdd={onAddColor}
      />

      <div className="flex items-center justify-between gap-2 px-3 pb-2">
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="touch-icon"
            aria-label={t('undo')}
            disabled={!canUndo}
            onClick={onUndo}
          >
            <Undo2Icon />
          </Button>
          <Button
            variant="ghost"
            size="touch-icon"
            aria-label={t('redo')}
            disabled={!canRedo}
            onClick={onRedo}
          >
            <Redo2Icon />
          </Button>
          <Button
            variant="ghost"
            size="touch-icon"
            aria-label={t('fitToScreen')}
            onClick={onFitToScreen}
          >
            <MaximizeIcon />
          </Button>
        </div>

        <Button variant="secondary" size="touch" disabled={!canAddRow} onClick={onAddRow}>
          <PlusIcon />
          {t('addRow')}
        </Button>
      </div>
    </footer>
  );
}
